import React from 'react';

import { Avatar } from 'antd';

import { getAvatarColor, getInitials } from '@/shared/lib/avatar';
import { Icon } from '@/shared/ui/Icon';
import { UserPopover } from '@/shared/ui/UserPopover';

import styles from './OwnerAvatars.module.css';

type Owner = {
  id: string;
  name: string;
  displayName?: string;
  type: string;
};

type OwnerAvatarsProps = {
  owners: Owner[];
  size?: number;
  showName?: boolean;
};

/**
 * 소유자/전문가 아바타 그룹 컴포넌트
 *
 * @description
 * 소유자나 전문가 목록을 겹쳐진 아바타로 표시합니다.
 * 1명 이상의 소유자를 모두 아바타로 표시하며, 각 아바타는 -6px씩 겹쳐집니다.
 *
 * @param owners - 소유자/전문가 목록
 * @param size - 아바타 크기 (기본값: 28)
 * @param showName - 이름 표시 여부 (기본값: false)
 *
 * @example
 * ```tsx
 * // 아바타만 표시 (하위 도메인, 검색 결과)
 * <OwnerAvatars owners={result.owners} size={28} />
 *
 * // 아바타 + 이름 표시 (미리보기 사이드바)
 * <OwnerAvatars owners={result.owners} size={28} showName={true} />
 * ```
 */
export const OwnerAvatars: React.FC<OwnerAvatarsProps> = ({ owners, size = 28, showName = false }) => {
  if (!owners || owners.length === 0) {
    return null;
  }

  const listClassName = showName
    ? `${styles.ownerList} ${styles.ownerListWithNames}`
    : `${styles.ownerList} ${styles.ownerListOverlapping}`;

  return (
    <div className={listClassName}>
      {owners.map((owner, index) => {
        const displayName = owner.displayName || owner.name;
        const avatarColor = getAvatarColor(owner.id);
        const isTeam = owner.type === 'team';
        const zIndex = owners.length - index;

        const itemClassName = showName ? styles.ownerItem : `${styles.ownerItem} ${styles.ownerItemOverlapping}`;

        if (isTeam) {
          return (
            <div key={owner.id} className={itemClassName} style={{ zIndex }}>
              <div className={styles.teamAvatarWrapper}>
                <Avatar
                  className={styles.teamAvatar}
                  size={size}
                  style={{ backgroundColor: avatarColor, borderColor: avatarColor }}>
                  <Icon name="users" size={14} />
                </Avatar>
              </div>
              <span className={styles.ownerName}>{displayName}</span>
            </div>
          );
        }

        return (
          <div key={owner.id} className={itemClassName} style={{ zIndex }}>
            <UserPopover
              userName={owner.name}
              avatarColor={avatarColor}
              initials={getInitials(displayName)}
              avatarSize={size}
              placement="topLeft"
              zIndex={zIndex}
              className={styles.ownerAvatar}
            />
            {showName && <span className={styles.ownerName}>{displayName}</span>}
          </div>
        );
      })}
    </div>
  );
};
